import type { QueryClient } from '@tanstack/react-query';
import type {
  ContributorPlaylistDto,
  ContributorRoleRefDto,
  FlowContributorPlaylistsDto,
  SessionContributorPlaylistsDto,
} from '@/types/dtos/contributors/contributor-playlist.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import type { SessionSummaryDto } from '@/types/dtos/sessions/response/session-summary.dto';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows.ts';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions.ts';
import { useFlowStore } from '@/state/stores/use-flow-store';

type WithFlowContributorPlaylists = {
  contributorPlaylists?: FlowContributorPlaylistsDto;
};

function mergePlaylistRefs(
  existing: ContributorRoleRefDto[] | undefined,
  updated: ContributorRoleRefDto[],
): ContributorRoleRefDto[] {
  const existingByRoleId = new Map((existing ?? []).map((ref) => [ref.roleId, ref]));
  return updated.map((ref) => ({
    ...existingByRoleId.get(ref.roleId),
    ...ref,
  }));
}

function mergeContributorPlaylist(
  existing: ContributorPlaylistDto | undefined,
  updated: ContributorPlaylistDto,
): ContributorPlaylistDto {
  return {
    ...existing,
    ...updated,
    refs: mergePlaylistRefs(existing?.refs, updated.refs),
  };
}

function replacePlaylistInFlowContributorPlaylists(
  contributorPlaylists: FlowContributorPlaylistsDto,
  updated: ContributorPlaylistDto,
): FlowContributorPlaylistsDto | null {
  if (contributorPlaylists.flow_moderators?.id !== updated.id) {
    return null;
  }
  return { flow_moderators: mergeContributorPlaylist(contributorPlaylists.flow_moderators, updated) };
}

function patchEntityWithFlowContributorPlaylists<T extends WithFlowContributorPlaylists>(
  entity: T,
  updated: ContributorPlaylistDto,
): T {
  if (!entity.contributorPlaylists) {
    return entity;
  }
  const contributorPlaylists = replacePlaylistInFlowContributorPlaylists(entity.contributorPlaylists, updated);
  if (!contributorPlaylists) {
    return entity;
  }
  return { ...entity, contributorPlaylists };
}

function patchSessionContributorPlaylists<T extends SessionDto | SessionSummaryDto>(
  session: T,
  updated: ContributorPlaylistDto,
): T {
  if (session.contributorPlaylists?.session?.id !== updated.id) {
    return session;
  }
  const contributorPlaylists: SessionContributorPlaylistsDto = {
    session: mergeContributorPlaylist(session.contributorPlaylists.session, updated),
  };
  return { ...session, contributorPlaylists };
}

function patchFlowQueryData(data: FlowDto | FlowDto[], updated: ContributorPlaylistDto): FlowDto | FlowDto[] {
  if (Array.isArray(data)) {
    return data.map((flow) => patchEntityWithFlowContributorPlaylists(flow, updated));
  }
  return patchEntityWithFlowContributorPlaylists(data, updated);
}

/** Flow detail UI reads playlists from Zustand; keep it in sync with playlist mutations. */
function patchFlowStoreWithContributorPlaylist(updated: ContributorPlaylistDto) {
  useFlowStore.setState((state) => {
    const patchFlowDocument = (flow?: FlowDto) => {
      if (!flow) {
        return flow;
      }
      return patchEntityWithFlowContributorPlaylists(flow, updated);
    };

    const flow = patchFlowDocument(state.flow);
    const flowPlan = patchFlowDocument(state.flowPlan);
    const flowOrganize = patchFlowDocument(state.flowOrganize);

    if (flow === state.flow && flowPlan === state.flowPlan && flowOrganize === state.flowOrganize) {
      return state;
    }

    return { flow, flowPlan, flowOrganize };
  });
}

export function patchContributorPlaylistInQueryCache(queryClient: QueryClient, updated: ContributorPlaylistDto) {
  for (const [queryKey, data] of queryClient.getQueriesData<FlowDto | FlowDto[]>({
    queryKey: [GET_FLOW_QUERY_KEY],
  })) {
    if (!data) continue;
    queryClient.setQueryData(queryKey, patchFlowQueryData(data, updated));
  }

  for (const [queryKey, data] of queryClient.getQueriesData<FlowDto[]>({
    queryKey: [GET_EVENT_FLOWS_QUERY_KEY],
  })) {
    if (!data || !Array.isArray(data)) continue;
    queryClient.setQueryData(
      queryKey,
      data.map((flow) => patchEntityWithFlowContributorPlaylists(flow, updated)),
    );
  }

  for (const [queryKey, data] of queryClient.getQueriesData<SessionDto>({
    queryKey: [GET_SESSION_QUERY_KEY],
  })) {
    if (!data) continue;
    queryClient.setQueryData(queryKey, patchSessionContributorPlaylists(data, updated));
  }

  for (const [queryKey, data] of queryClient.getQueriesData<SessionSummaryDto[]>({
    queryKey: [GET_EVENT_SESSIONS_QUERY_KEY],
  })) {
    if (!data || !Array.isArray(data)) continue;
    queryClient.setQueryData(
      queryKey,
      data.map((session) => patchSessionContributorPlaylists(session, updated)),
    );
  }

  patchFlowStoreWithContributorPlaylist(updated);
}
